import { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Statistic,
  Typography,
  Spin,
  Alert,
  Button,
} from 'antd';
import {
  UserOutlined,
  DatabaseOutlined,
  SettingOutlined,
  // BarChartOutlined,
  FileTextOutlined,
  // TeamOutlined,
  EyeOutlined,
  AppstoreOutlined,
  ShoppingOutlined,
  PictureOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { Service } from '@/apis';
import type { DashboardAllResponse } from '@/apis';
import styles from './index.module.less';

const { Title } = Typography;

// 格式化时间函数
const formatTime = (timestamp: number | string | undefined): string => {
  if (!timestamp) return '--:--';

  try {
    // 处理不同的时间戳格式
    let date: Date;
    if (typeof timestamp === 'string') {
      // 如果是字符串，尝试解析
      date = new Date(timestamp);
    } else {
      // 如果是数字，检查是否是毫秒还是秒
      const ts = timestamp > 10000000000 ? timestamp : timestamp * 1000;
      date = new Date(ts);
    }

    // 检查日期是否有效
    if (isNaN(date.getTime())) {
      return '--:--';
    }

    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  } catch (error) {
    console.error('时间格式化错误:', error, 'timestamp:', timestamp);
    return '--:--';
  }
};

const App = () => {
  const navigate = useNavigate();
  // 状态管理
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [dashboardData, setDashboardData] =
    useState<DashboardAllResponse | null>(null);

  // 获取dashboard数据
  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await Service.dashboardControllerGetAllDashboardData();
      console.log('Dashboard API Response:', response);

      if (response && response.data) {
        console.log('Activities data:', response.data.activities);
        console.log(
          'Activities activities:',
          response.data.activities?.activities
        );
        setDashboardData(response.data);
        console.log('Dashboard data loaded successfully');
      } else {
        throw new Error('API返回数据格式错误');
      }
    } catch (err) {
      console.error('Dashboard API Error:', err);
      setError('获取数据失败，请稍后重试');
      // 使用模拟数据作为fallback
      setDashboardData({
        overview: {
          totalSystemAccounts: 1234,
          totalSystemLogs: 256,
          totalUsers: 5678,
          totalUserLogs: 89,
        },
        activities: {
          activities: [
            {
              timestamp: Date.now() - 30 * 60 * 1000,
              time: formatTime(Date.now() - 30 * 60 * 1000),
              description: '用户 admin 登录系统',
              type: 'login',
              module: 'auth',
              action: 'login',
              userId: 'admin_001',
              username: 'admin',
              userRole: 'administrator',
            },
            {
              timestamp: Date.now() - 75 * 60 * 1000,
              time: formatTime(Date.now() - 75 * 60 * 1000),
              description: '数据备份任务完成',
              type: 'system_info',
              module: 'backup',
              action: 'complete',
              userId: 'system_001',
              username: 'system',
              userRole: 'system',
            },
            {
              timestamp: Date.now() - 105 * 60 * 1000,
              time: formatTime(Date.now() - 105 * 60 * 1000),
              description: '系统配置已更新',
              type: 'operation',
              module: 'config',
              action: 'update',
              userId: 'manager_001',
              username: 'manager',
              userRole: 'manager',
            },
          ],
        },
        status: {
          databaseConnection: {
            status: 'normal',
            message: '正常',
          },
          serverStatus: {
            status: 'running',
            message: '运行中',
          },
          storageSpace: {
            status: 'normal',
            message: '75% 已使用',
            usage: '75%',
          },
        },
      } as DashboardAllResponse);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // 测试时间格式化函数
    console.log('测试时间格式化:');
    console.log('当前时间:', formatTime(Date.now()));
    console.log('字符串时间:', formatTime('2024-01-01T10:30:00Z'));
    console.log('秒时间戳:', formatTime(1704112200));
    console.log('毫秒时间戳:', formatTime(1704112200000));

    fetchDashboardData();
  }, []);

  // 统计数据配置
  const statsData = dashboardData
    ? [
        {
          title: '系统账号总数',
          value: dashboardData.overview.totalSystemAccounts,
          icon: <UserOutlined />,
          color: '#1890ff',
        },
        {
          title: '系统日志',
          value: dashboardData.overview.totalSystemLogs,
          icon: <FileTextOutlined />,
          color: '#faad14',
        },
        {
          title: '用户总数',
          value: dashboardData.overview.totalUsers,
          icon: <DatabaseOutlined />,
          color: '#52c41a',
        },
        {
          title: '用户日志',
          value: dashboardData.overview.totalUserLogs,
          icon: <FileTextOutlined />,
          color: '#f5222d',
        },
      ]
    : [];

  const quickActions = [
    {
      title: '用户管理',
      icon: <UserOutlined />,
      path: '/system/manager',
    },
    // {
    //   title: '角色管理',
    //   icon: <TeamOutlined />,
    //   path: '/system/managerRole',
    // },
    // {
    //   title: '权限管理',
    //   icon: <SettingOutlined />,
    //   path: '/system/managerPermission',
    // },
    // {
    //   title: '数据管理',
    //   icon: <DatabaseOutlined />,
    //   path: '/data/dataMark',
    // },
    // {
    //   title: '数据看板',
    //   icon: <BarChartOutlined />,
    //   path: '/data/dashboard',
    // },
    {
      title: '分类管理',
      icon: <AppstoreOutlined />,
      path: '/cms/category',
    },
    {
      title: '产品管理',
      icon: <ShoppingOutlined />,
      path: '/cms/product',
    },
    {
      title: '新闻管理',
      icon: <FileTextOutlined />,
      path: '/cms/news',
    },
    {
      title: '轮播管理',
      icon: <PictureOutlined />,
      path: '/cms/banner',
    },
    {
      title: '站点配置',
      icon: <SettingOutlined />,
      path: '/cms/siteConfig',
    },
    {
      title: '常见问题',
      icon: <QuestionCircleOutlined />,
      path: '/cms/faq',
    },
  ];

  if (loading) {
    return (
      <div className={styles.main}>
        <div className={styles.container}>
          <div style={{ textAlign: 'center', padding: '100px 0' }}>
            <Spin size='large' />
            <div style={{ marginTop: '20px', fontSize: '16px', color: '#666' }}>
              正在加载系统数据...
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.main}>
      <div className={styles.container}>
        {/* 错误提示 */}
        {error && (
          <Alert
            message='数据加载异常'
            description={error}
            type='warning'
            showIcon
            closable
            style={{ marginBottom: '24px' }}
            onClose={() => setError('')}
          />
        )}

        {/* 统计卡片 */}
        <div className={styles.statsSection}>
          <Title level={3}>系统概览</Title>
          <Row gutter={[16, 16]}>
            {statsData.map((stat, index) => (
              <Col xs={24} sm={12} md={6} key={index}>
                <Card
                  className={styles.statCard}
                  style={{
                    animationDelay: `${index * 0.1}s`,
                    animationFillMode: 'both',
                  }}
                >
                  <Statistic
                    title={stat.title}
                    value={stat.value}
                    prefix={
                      <span style={{ color: stat.color }}>{stat.icon}</span>
                    }
                    valueStyle={{ color: stat.color }}
                  />
                </Card>
              </Col>
            ))}
          </Row>
        </div>

        {/* 快捷操作 */}
        <div className={styles.actionsSection}>
          <Title level={3}>快捷操作</Title>
          <Row gutter={[16, 16]}>
            {quickActions.map((action, index) => (
              <Col xs={24} sm={12} md={8} lg={4} key={index}>
                <Card
                  className={styles.actionCard}
                  hoverable
                  style={{
                    animationDelay: `${index * 0.1}s`,
                    animationFillMode: 'both',
                  }}
                  onClick={() => {
                    navigate(action.path);
                  }}
                >
                  <div className={styles.actionContent}>
                    <div className={styles.actionIcon}>{action.icon}</div>
                    <div className={styles.actionTitle}>{action.title}</div>
                  </div>
                </Card>
              </Col>
            ))}
          </Row>
        </div>

        {/* 系统信息 */}
        <div className={styles.infoSection}>
          <Row gutter={[16, 16]}>
            <Col xs={24} md={12}>
              <Card
                title='最近活动'
                className={styles.infoCard}
                extra={
                  <Button
                    type='link'
                    size='small'
                    icon={<EyeOutlined />}
                    onClick={() => navigate('/system/systemLog')}
                    style={{ color: '#1890ff' }}
                  >
                    查看更多
                  </Button>
                }
              >
                <div className={styles.activityList}>
                  {dashboardData?.activities?.activities?.map(
                    (activity, index) => {
                      console.log('Activity item:', activity);
                      const displayTime =
                        activity.time || formatTime(activity.timestamp);
                      console.log(
                        'Display time:',
                        displayTime,
                        'for activity:',
                        activity.description
                      );

                      return (
                        <div key={index} className={styles.activityItem}>
                          <span className={styles.activityTime}>
                            {displayTime}
                          </span>
                          <span className={styles.activityDesc}>
                            {activity.description || '未知活动'}
                          </span>
                          <span className={styles.activityOperator}>
                            {activity.username || '系统'}
                          </span>
                        </div>
                      );
                    }
                  ) || (
                    <div className={styles.activityItem}>
                      <span className={styles.activityTime}>--:--</span>
                      <span className={styles.activityDesc}>暂无活动数据</span>
                    </div>
                  )}
                </div>
              </Card>
            </Col>
            <Col xs={24} md={12}>
              <Card title='系统状态' className={styles.infoCard}>
                <div className={styles.statusList}>
                  <div className={styles.statusItem}>
                    <span className={styles.statusLabel}>数据库连接</span>
                    <span className={styles.statusValue}>
                      {dashboardData?.status.databaseConnection.message}
                    </span>
                  </div>
                  <div className={styles.statusItem}>
                    <span className={styles.statusLabel}>服务器状态</span>
                    <span className={styles.statusValue}>
                      {dashboardData?.status.serverStatus.message}
                    </span>
                  </div>
                  <div className={styles.statusItem}>
                    <span className={styles.statusLabel}>存储空间</span>
                    <span className={styles.statusValue}>
                      {dashboardData?.status.storageSpace.message}
                    </span>
                  </div>
                </div>
              </Card>
            </Col>
          </Row>
        </div>
      </div>
    </div>
  );
};

export default App;
